const BASE = (process.env.REACT_APP_SENTIMENT_API || 'https://dev-agent.savictech.com/sm/api/v1').replace(/\/$/, '');

function getToken() { return localStorage.getItem('sentiment_token') || ''; }
function authHeaders(extra = {}) {
  return { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...extra };
}

async function handleResponse(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || data.message || `HTTP ${res.status}`);
  return data;
}

function buildQs(params = {}) {
  const qs = Object.entries(params)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return qs ? `?${qs}` : '';
}

// Auth
export async function login(email, password) {
  const res = await fetch(`${BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  return handleResponse(res);
}

// Dashboard
export async function fetchKpis() {
  const res = await fetch(`${BASE}/dashboard/kpis`, { headers: authHeaders() });
  return handleResponse(res);
}

export async function fetchCustomers(params = {}) {
  const res = await fetch(`${BASE}/dashboard/customers${buildQs(params)}`, { headers: authHeaders() });
  return handleResponse(res);
}

// Alerts
export async function fetchAlerts(params = {}) {
  const res = await fetch(`${BASE}/alerts/${buildQs(params)}`, { headers: authHeaders() });
  return handleResponse(res);
}

export async function createAlert(body) {
  const res = await fetch(`${BASE}/alerts/create`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

export async function updateAlert(alertId, body) {
  const res = await fetch(`${BASE}/alerts/${alertId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

export async function assignAlert(alertId, assignedTo) {
  const res = await fetch(`${BASE}/alerts/${alertId}/assign`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ assigned_to: assignedTo }),
  });
  return handleResponse(res);
}

// Workflows
export async function fetchWorkflow(alertId) {
  const res = await fetch(`${BASE}/workflows/${alertId}`, { headers: authHeaders() });
  return handleResponse(res);
}

export async function updateWorkflowStep(stepId, body) {
  const res = await fetch(`${BASE}/workflows/steps/${stepId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

// Automations
export async function fetchProposals(status = '') {
  const res = await fetch(`${BASE}/automations/proposals${buildQs({ status, limit: 50 })}`, { headers: authHeaders() });
  return handleResponse(res);
}

export async function reviewProposal(proposalId, body) {
  const res = await fetch(`${BASE}/automations/proposals/${proposalId}/review`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

// Copilot — generate is a POST (triggers generation), action is a POST
export async function fetchRecommendations() {
  const res = await fetch(`${BASE}/copilot/recommendations/generate`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return handleResponse(res);
}

export async function actionRecommendation(id, action, appliedBy) {
  const res = await fetch(`${BASE}/copilot/recommendations/action`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ recommendation_id: id, action, applied_by: appliedBy }),
  });
  return handleResponse(res);
}

// Analytics
export async function fetchTrends(params = {}) {
  const res = await fetch(`${BASE}/analytics/trends${buildQs(params)}`, { headers: authHeaders() });
  return handleResponse(res);
}

export async function exportAnalyticsCsv(days = 30) {
  const res = await fetch(`${BASE}/analytics/export/csv${buildQs({ days })}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

// Audit
export async function fetchAudit(params = {}) {
  const res = await fetch(`${BASE}/audit${buildQs(params)}`, { headers: authHeaders() });
  return handleResponse(res);
}

export async function exportAuditCsv(days = 30) {
  const res = await fetch(`${BASE}/audit/export/csv${buildQs({ days })}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

// Users
export async function fetchUsers() {
  const res = await fetch(`${BASE}/users`, { headers: authHeaders() });
  return handleResponse(res);
}

export async function createUser(body) {
  const res = await fetch(`${BASE}/users`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

export async function updateUser(userId, body) {
  const res = await fetch(`${BASE}/users/${userId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

// RBAC
export async function fetchPermissions() {
  const res = await fetch(`${BASE}/rbac/permissions`, { headers: authHeaders() });
  return handleResponse(res);
}

export async function updatePermission(body) {
  const res = await fetch(`${BASE}/rbac/permissions`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

// Comments
export async function fetchComments(params = {}) {
  const res = await fetch(`${BASE}/comments${buildQs(params)}`, { headers: authHeaders() });
  return handleResponse(res);
}

export async function createComment(body) {
  const res = await fetch(`${BASE}/comments`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

// Reports
export async function createReport(body) {
  const res = await fetch(`${BASE}/reports`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

// ── Monitoring (same backend as BASE) ────────────────────────────────────────

export async function fetchMonitoringConfig() {
  const res = await fetch(`${BASE}/monitoring/config`, { headers: authHeaders() });
  return handleResponse(res);
}

export async function updateMonitoringConfig(body) {
  const res = await fetch(`${BASE}/monitoring/config`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

export async function fetchMonitoringEntities() {
  const res = await fetch(`${BASE}/monitoring/entities`, { headers: authHeaders() });
  return handleResponse(res);
}

export async function addMonitoringEntity(body) {
  const res = await fetch(`${BASE}/monitoring/entities`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  if (res.status === 409) throw new Error('This email or domain is already in the monitored list.');
  return handleResponse(res);
}

export async function toggleMonitoringEntity(entityId, isActive) {
  const res = await fetch(
    `${BASE}/monitoring/entities/${entityId}/toggle?is_active=${isActive}`,
    { method: 'PATCH', headers: authHeaders() }
  );
  return handleResponse(res);
}

export async function deleteMonitoringEntity(entityId) {
  const res = await fetch(`${BASE}/monitoring/entities/${entityId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || data.message || `HTTP ${res.status}`);
  }
  // 204 No Content — no body
}
